// UnifiedNavigation.jsx - Clean inline styles matching target design
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, Target, FileText, Shield, Zap,
  Settings, User, LogOut, ChevronDown, ChevronRight,
  Building2, Compass, Users, Calculator, HelpCircle
} from 'lucide-react';
import ApertureLogo from './ApertureLogo';

const UnifiedNavigation = ({ currentApp = 'defense', currentPage = '' }) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    dashboard: true,
    executive: false,
    defense: true,
    coordinator: false
  });
  
  // Get user role to show appropriate navigation items
  const userRole = localStorage.getItem('userRole') || 'coordinator';
  const isViewer = userRole === 'demo_viewer';

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const navigateToApp = (url) => {
    if (url === '/logout') {
      // Handle logout - clear auth and session state from both storages
      ['isAuthenticated', 'userRole', 'activeSessionId', 'currentSessionId',
       'authToken', 'token', 'foundations-token', 'userId', 'userEmail',
       'companyName', 'companyId', 'isDemoViewer', 'defenseAuthToken'].forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      navigate('/login');
    } else {
      // All navigation is now internal with React Router
      navigate(url);
    }
  };

  // Get sessionId from current URL or localStorage
  const getCurrentSessionId = () => {
    const pathParts = window.location.pathname.split('/');
    if (pathParts[1] === 'assessment' && pathParts[2]) {
      localStorage.setItem('activeSessionId', pathParts[2]);
      return pathParts[2];
    }
    if (pathParts[1] === 'persona-assessment' && pathParts[2]) {
      localStorage.setItem('activeSessionId', pathParts[2]);
      return pathParts[2];
    }
    return localStorage.getItem('activeSessionId') || localStorage.getItem('currentSessionId') || null;
  };

  const currentSessionId = getCurrentSessionId();
  const sessionParam = currentSessionId ? `?sessionId=${currentSessionId}` : '';

  // Build navigation sections based on user role and current app
  const navigationSections = [];

  // Dashboard section - always visible regardless of current module
  if (userRole === 'portfolio_manager') {
      navigationSections.push({
        id: 'dashboard',
        title: 'Portfolio Management',
        icon: Building2,
        app: 'foundations',
        items: [
          { name: 'Portfolio Overview', url: '/portfolio-dashboard', active: currentApp === 'foundations' && currentPage === 'portfolio' },
          { name: 'All Assessments', url: '/portfolio-dashboard?view=assessments', active: currentApp === 'foundations' && currentPage === 'assessments' },
          { name: 'Risk Overview', url: '/portfolio-dashboard?view=risk', active: currentApp === 'foundations' && currentPage === 'risk' },
          { name: 'Companies', url: '/portfolio-dashboard?view=companies', active: currentApp === 'foundations' && currentPage === 'companies' }
        ]
      });
    } else {
      // For coordinator and other roles, show current assessment dashboard
      const dashboardItems = [
        { name: 'Current Assessment', url: '/coordinator-dashboard', active: currentApp === 'foundations' && currentPage === 'coordinator' }
      ];
      if (currentSessionId) {
        dashboardItems.push({
          name: 'Assessment Results',
          url: `/assessment/${currentSessionId}/results`,
          active: currentApp === 'foundations' && currentPage === 'assessment'
        });
      }
      navigationSections.push({
        id: 'dashboard',
        title: 'Dashboard',
        icon: Building2,
        app: 'foundations',
        items: dashboardItems
      });
  }

  // Only show session-based navigation sections for non-portfolio-manager roles
  // Portfolio managers should only see their portfolio management section
  if (userRole !== 'portfolio_manager') {
    navigationSections.push(
      {
        id: 'executive',
        title: 'Executive Intelligence',
        icon: Compass,
        app: 'compass',
        items: [
          { name: 'Business Intelligence', url: `/intelligence${sessionParam}`, active: currentApp === 'compass' && currentPage === 'business-intelligence' },
          { name: 'Outside Pressures Analysis', url: `/intelligence/outside-pressures${sessionParam}`, active: currentApp === 'compass' && currentPage === 'outside-pressures' },
          { name: 'Investment Optimization', url: `/intelligence/investment${sessionParam}`, active: currentApp === 'compass' && currentPage === 'investment-optimization' },
          { name: 'Strategic Scenarios', url: `/intelligence/scenarios${sessionParam}`, active: currentApp === 'compass' && currentPage === 'strategic-scenarios' },
          { name: 'Board Reports', url: `/intelligence/reports${sessionParam}`, active: currentApp === 'compass' && currentPage === 'board-reports' }
        ]
      },
      {
        id: 'defense',
        title: 'Risk Optimization Studio',
        icon: Shield,
        app: 'defense',
        items: [
          { name: 'Friction Analysis', url: '/studio', active: currentApp === 'defense' && currentPage === '' },
          { name: 'Financial Impact Analysis', url: '/studio/executive', active: currentApp === 'defense' && currentPage === 'executive-simulation' }
        ]
      }
    );

    // Demo viewers don't see Coordinator section (input forms)
    if (!isViewer) {
      navigationSections.push({
        id: 'coordinator',
        title: 'Coordinator',
        icon: Users,
        app: 'foundations',
        items: [
          { name: 'Persona Dashboard', url: '/coordinator-dashboard', active: currentApp === 'foundations' && currentPage === 'coordinator' }
        ]
      });
    }
  }

  const bottomNavItems = isViewer
    ? [
        { name: 'Help', icon: HelpCircle, url: '/help' },
        { name: 'Exit Demo', icon: LogOut, url: '/logout' },
      ]
    : [
        { name: 'Help', icon: HelpCircle, url: '/help' },
        { name: 'Settings', icon: Settings, url: '/settings' },
        { name: 'Profile', icon: User, url: '/profile' },
        { name: 'Logout', icon: LogOut, url: '/logout' }
      ];

  // Inline styles matching target design exactly
  const sidebarStyle = {
    width: '320px',
    minHeight: '100vh',
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    background: 'var(--ra-gradient-sidebar)',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--ra-glass-border)'
  };

  const headerStyle = {
    padding: '16px',
    borderBottom: '1px solid var(--ra-glass-border)'
  };

  const headerContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const logoStyle = {
    fontSize: '18px',
    fontWeight: '700',
    margin: '0'
  };

  const subtitleStyle = {
    fontSize: '12px',
    color: 'var(--ra-text-secondary)',
    margin: '0'
  };

  const navStyle = {
    padding: '8px'
  };

  const sectionStyle = {
    marginBottom: '8px'
  };

  const getSectionButtonStyle = (isCurrentApp, isExpanded) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: isCurrentApp ? 'rgba(0, 217, 255, 0.12)' : 'transparent',
    borderLeft: isCurrentApp ? '3px solid var(--ra-cyber-aqua)' : '3px solid transparent',
    color: isCurrentApp ? 'var(--ra-cyber-aqua)' : 'var(--ra-text-secondary)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  });

  const sectionContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const itemsStyle = {
    marginLeft: '16px',
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  };

  const getItemButtonStyle = (isActive) => ({
    width: '100%',
    textAlign: 'left',
    padding: '8px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: isActive ? 'rgba(0, 217, 255, 0.12)' : 'transparent',
    color: isActive ? 'var(--ra-cyber-aqua)' : 'var(--ra-text-muted)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease'
  });

  const bottomNavStyle = {
    borderTop: '1px solid var(--ra-glass-border)',
    padding: '8px'
  };

  const bottomItemStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--ra-text-muted)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease'
  };

  const statusStyle = {
    padding: '8px',
    borderTop: '1px solid var(--ra-glass-border)',
    textAlign: 'center',
    fontSize: '12px',
    color: 'var(--ra-text-muted)'
  };

  return (
    <nav data-tour="primary-nav" style={sidebarStyle} aria-label="Primary">
      {/* Header - clickable to go home */}
      <div style={headerStyle}>
        <div
          role="link"
          tabIndex={0}
          style={{ ...headerContentStyle, cursor: 'pointer' }}
          onClick={() => navigateToApp(
            currentSessionId ? `/assessment/${currentSessionId}/results` : '/coordinator-dashboard'
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigateToApp(currentSessionId ? `/assessment/${currentSessionId}/results` : '/coordinator-dashboard');
            }
          }}
          title="Go to Assessment Results"
          aria-label="Go to Assessment Results"
        >
          <ApertureLogo size={32} color="var(--ra-cyber-aqua, #00d9ff)" />
          <div>
            <h1 style={logoStyle}>FOUNDATIONS</h1>
            <p style={subtitleStyle}>Risk Aperture Suite</p>
          </div>
        </div>
      </div>

      {/* Navigation Sections */}
      <div style={navStyle}>
        {navigationSections.map((section) => {
          // AI Assistant section removed - using page-embedded AI assistants

          const Icon = section.icon;
          const isExpanded = expandedSections[section.id];
          const hasActiveChild = section.items.some(item => item.active);
          const isCurrentApp = hasActiveChild;

          return (
            <div key={section.id} style={sectionStyle}>
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                style={getSectionButtonStyle(isCurrentApp, isExpanded)}
                onMouseEnter={(e) => {
                  if (!isCurrentApp) {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentApp) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={sectionContentStyle}>
                  <Icon size={20} />
                  <span>{section.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown aria-hidden="true" size={16} />
                ) : (
                  <ChevronRight aria-hidden="true" size={16} />
                )}
              </button>

              {/* Section Items */}
              {isExpanded && (
                <div style={itemsStyle}>
                  {section.items.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => navigateToApp(item.url)}
                      style={getItemButtonStyle(item.active)}
                      onMouseEnter={(e) => {
                        if (!item.active) {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                          e.target.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!item.active) {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#9ca3af';
                        }
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Spacer to push bottom nav to bottom */}
      <div style={{ flex: 1 }}></div>

      {/* Bottom Navigation */}
      <div style={bottomNavStyle}>
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.name}
              onClick={() => navigateToApp(item.url)}
              style={bottomItemStyle}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#9ca3af';
              }}
            >
              <Icon size={20} />
              <span>{item.name}</span>
            </button>
          );
        })}
      </div>

      {/* App Status Indicator */}
      <div style={statusStyle}>
        Current: <span style={{ color: 'var(--ra-cyber-aqua)', textTransform: 'capitalize' }}>{currentApp}</span>
        {currentPage && <span> → {currentPage}</span>}
      </div>
    </nav>
  );
};

export default UnifiedNavigation;